import React from "react";
import { Box, Typography, Stack, Tooltip } from "@mui/material";
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { formatMoney } from "../../producer-dashboard/ProducerDashboardHelper";

type FlowRowProps = {
    label: string;
    amount: number;
    type: 'in' | 'out';
    currency: string;
    tooltip?: string;
    details?: any;
};

export const FlowRow = ({ label, amount, type, currency, tooltip, details }: FlowRowProps) => {
    const ready = details?.ready || 0;
    const awaiting = details?.awaiting_dependency || details?.awaitingDependency || 0;
    const blocked = details?.blocked || 0;
    const hasDetails = details && (ready > 0 || awaiting > 0 || blocked > 0);

    return (
        <Box sx={{ py: 1, borderBottom: '1px dashed #f1f5f9' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Typography variant="body2" color="text.secondary" fontWeight={600}>{label}</Typography>
                    {tooltip && (
                        <Tooltip title={tooltip} arrow placement="top">
                            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'pointer' }} />
                        </Tooltip>
                    )}
                </Stack>
                <Stack direction="row" spacing={0.5} alignItems="baseline">
                    {hasDetails && <Typography variant="caption" color="text.disabled" sx={{ mr: 0.5 }}>Total:</Typography>}
                    <Typography variant="body2" sx={{ fontWeight: 700, color: type === 'in' ? 'success.main' : 'error.main' }}>
                        {type === 'in' ? '+' : '-'}{formatMoney(amount, currency)}
                    </Typography>
                    {type === 'in' ? (
                        <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main', position: 'relative', top: 3 }} />
                    ) : (
                        <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main', position: 'relative', top: 3 }} />
                    )}
                </Stack>
            </Stack>
            {hasDetails && (
                <Box sx={{ pl: 1, mt: 0.5, borderLeft: '2px solid #e2e8f0', ml: 0.5 }}>
                    {ready > 0 && (
                        <Typography variant="caption" display="block" sx={{ color: '#10b981', fontSize: '0.65rem', lineHeight: 1.4 }}>
                            ↳ Ready to process: {formatMoney(ready, currency)}
                        </Typography>
                    )}
                    {awaiting > 0 && (
                        <Typography variant="caption" display="block" sx={{ color: '#f59e0b', fontSize: '0.65rem', lineHeight: 1.4 }}>
                            ↳ Awaiting dependencies: {formatMoney(awaiting, currency)}
                        </Typography>
                    )}
                    {blocked > 0 && (
                        <Typography variant="caption" display="block" sx={{ color: '#ef4444', fontSize: '0.65rem', lineHeight: 1.4 }}>
                            ↳ Blocked: {formatMoney(blocked, currency)}
                        </Typography>
                    )}
                </Box>
            )}
        </Box>
    );
};